/**
 * Similar Evidence Modal
 * Displays semantically similar documents for a given evidence item
 */

import React, { useState, useCallback } from 'react';
import { 
  X, Search, Sparkles, FileText, ExternalLink, 
  ChevronRight, Loader2, AlertTriangle, CheckCircle 
} from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { apiFetch } from '../services/api_service';


// Similarity level styles
const SIMILARITY_STYLES = {
  high: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'High Match' },
  medium: { bg: 'bg-amber-500/20', text: 'text-amber-400', label: 'Medium Match' },
  low: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Low Match' },
  minimal: { bg: 'bg-[#2F2F2F]', text: 'text-[#6B6B6B]', label: 'Minimal' },
};

export function SimilarEvidenceModal({ isOpen, onClose, evidence, onSelectEvidence }) {
  const [similarDocs, setSimilarDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);

  const getToken = () => localStorage.getItem('token');

  // Search for similar documents
  const searchSimilar = useCallback(async () => {
    if (!evidence) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiFetch(`/v1/idp/find-similar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          document_id: evidence.id,
          top_k: 10,
          min_similarity: 0.4
        })
      });

      if (!response.ok) {
        // Try semantic search with text content instead
        const textContent = evidence.extracted_text || evidence.description || evidence.original_filename;
        if (textContent) {
          const searchResponse = await apiFetch(`/v1/idp/search`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${getToken()}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              query: textContent,
              top_k: 10,
              min_similarity: 0.4
            })
          });
          
          if (searchResponse) {
            const data = await searchResponse.json();
            // Filter out the current document
            const filtered = data.results?.filter(r => r.document_id !== evidence.id) || [];
            setSimilarDocs(filtered);
            setSearched(true);
            return;
          }
        }
        throw new Error('Failed to find similar documents');
      }

      const data = await response.json();
      setSimilarDocs(data.similar_documents || []);
      setSearched(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [evidence]);

  // Reset state when modal opens
  React.useEffect(() => {
    if (isOpen && evidence) {
      setSimilarDocs([]);
      setSearched(false);
      setError(null);
      // Auto-search when modal opens
      searchSimilar();
    }
  }, [isOpen, evidence, searchSimilar]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div 
        className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-xl w-full max-w-2xl max-h-[80vh] overflow-hidden shadow-2xl"
        data-testid="similar-evidence-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#2F2F2F]">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <Sparkles className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Find Similar Evidence</h2>
              <p className="text-sm text-[#6B6B6B]">AI-powered semantic similarity search</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-[#2F2F2F] rounded-lg transition-colors"
            data-testid="close-similar-modal"
          >
            <X className="w-5 h-5 text-[#6B6B6B]" />
          </button>
        </div>

        {/* Reference Document */}
        <div className="p-4 bg-[#0D0D0D] border-b border-[#2F2F2F]">
          <p className="text-xs text-[#6B6B6B] mb-2">Finding documents similar to:</p>
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-emerald-400" />
            <div>
              <p className="text-sm font-medium text-white">{evidence?.original_filename || evidence?.name}</p>
              <p className="text-xs text-[#6B6B6B]">{evidence?.category || evidence?.evidence_type}</p>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="p-4 overflow-y-auto max-h-[400px]">
          {loading && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-purple-400 animate-spin mb-4" />
              <p className="text-[#6B6B6B]">Analyzing document similarity...</p>
            </div>
          )}

          {error && (
            <div className="flex flex-col items-center justify-center py-12">
              <AlertTriangle className="w-8 h-8 text-amber-400 mb-4" />
              <p className="text-[#6B6B6B] mb-2">Unable to find similar documents</p>
              <p className="text-xs text-[#4B4B4B]">{error}</p>
              <Button 
                onClick={searchSimilar}
                variant="outline" 
                size="sm"
                className="mt-4 border-[#2F2F2F]"
              >
                Try Again
              </Button>
            </div>
          )}

          {!loading && !error && searched && similarDocs.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12">
              <Search className="w-8 h-8 text-[#4B4B4B] mb-4" />
              <p className="text-[#6B6B6B] mb-2">No similar documents found</p>
              <p className="text-xs text-[#4B4B4B]">This document appears to be unique in your evidence library</p>
            </div>
          )}

          {!loading && !error && similarDocs.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-[#6B6B6B]">
                  Found <span className="text-white font-medium">{similarDocs.length}</span> similar documents
                </p>
              </div>

              {similarDocs.map((doc, idx) => {
                const similarity = SIMILARITY_STYLES[doc.similarity_level] || SIMILARITY_STYLES.minimal;
                const scorePercent = Math.round(doc.similarity_score * 100);

                return (
                  <div 
                    key={doc.document_id || idx}
                    className="p-4 bg-[#0D0D0D] border border-[#2F2F2F] rounded-lg hover:border-purple-500/30 transition-all cursor-pointer group"
                    onClick={() => onSelectEvidence?.(doc.document_id)}
                    data-testid={`similar-doc-${idx}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-[#6B6B6B] group-hover:text-purple-400" />
                        <div>
                          <p className="text-sm font-medium text-white group-hover:text-purple-400 transition-colors">
                            {doc.document_name}
                          </p>
                          <p className="text-xs text-[#6B6B6B]">{doc.document_type}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={`${similarity.bg} ${similarity.text} border-0`}>
                          {scorePercent}% {similarity.label}
                        </Badge>
                        <ChevronRight className="w-4 h-4 text-[#4B4B4B] group-hover:text-purple-400" />
                      </div>
                    </div>

                    {/* Framework tags */}
                    {doc.framework_codes?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {doc.framework_codes.map(fw => (
                          <span 
                            key={fw} 
                            className="px-2 py-0.5 text-[10px] bg-[#1F1F1F] text-[#A0A0A0] rounded"
                          >
                            {fw}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Preview text */}
                    {doc.text_preview && (
                      <p className="text-xs text-[#4B4B4B] mt-2 line-clamp-2">
                        {doc.text_preview}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-[#2F2F2F] bg-[#0D0D0D]">
          <div className="flex items-center justify-between">
            <p className="text-xs text-[#4B4B4B]">
              Powered by semantic vector embeddings (384-dim)
            </p>
            <Button 
              onClick={onClose}
              variant="outline"
              size="sm"
              className="border-[#2F2F2F]"
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SimilarEvidenceModal;
